<template>
  <v-dialog :model-value="value" width="500px" @click:outside="closeModal">
    <BaseDialogCard v-if="value" :title="tkey('modalTitle')" @close="closeModal">
      <v-container fluid class="pa-0 clean-attributes__container">
        <v-row class="mt-4 border-bottom">
          <p class="mb-4">
            {{ cleanAttributesModalDescription }}
          </p>
        </v-row>

        <div class="mt-4 pb-5 border-bottom">
          <v-row>
            <span class="font-weight-bold">
              {{ tkey('attributes') }}
            </span>
          </v-row>
          <v-row v-for="attr in customAttributes" :key="attr.id" class="checkbox-selection-row">
            <v-checkbox
              :model-value="isAttributeSelected(attr.id)"
              class="rtls-checkbox mx-2"
              @update:model-value="updateCurrentSelections(attr.id, $event)"
            />
            <span>{{ attr.name }}</span>
          </v-row>
        </div>

        <v-row class="mt-4 align-center">
          <v-checkbox
            class="rtls-checkbox mx-2 ml-3"
            :model-value="autoCleanOnSave"
            @update:model-value="autoCleanOnSave = $event"
          />
          <span class="mr-4">{{ tkey('autoClean') }}</span>
        </v-row>
      </v-container>
      <template #footer>
        <PageActions
          :has-data-errors="hasDataErrors"
          :has-data-changes="true"
          :show-discard="false"
          save-btn-text="saveSettings"
          @save="onSave"
        >
          <template #right-btns>
            <v-btn class="bg-primary" :disabled="isCleanNowDisabled" @click="cleanAttributes">
              {{ $t('actions.cleanNow') }}
            </v-btn>
          </template>
        </PageActions>
      </template>
    </BaseDialogCard>
  </v-dialog>
</template>

<script>
import { useInternationalization } from '@/composables/use-internationalization';
import { useContextStore } from '@/stores/context';
import { useScenariosStore } from '@/stores/scenarios';
import { each, get, some } from 'lodash';
import { mapState } from 'pinia';

export default {
  props: {
    value: {
      type: Boolean,
      required: true,
    },

    customAttributes: {
      type: Object,
      required: true,
    },

    attributesToClean: {
      type: Set,
      required: true,
    },
  },
  setup() {
    const { tkey } = useInternationalization('scenarioInputPage.cleanAttributesModal');
    return { tkey };
  },

  data() {
    return {
      autoCleanOnSave: false,
      currentSelections: {},
    };
  },

  computed: {
    ...mapState(useContextStore, ['clientConfig', 'getClientConfig']),
    ...mapState(useScenariosStore, ['selectedScenario']),

    cleanAttributesModalDescription() {
      return this.tkey('description', {
        case: this.tkey(
          `formattingOptions.${this.hasCleanAttributesUpperCaseEnabled ? 'upperCase' : 'lowerCase'}`
        ),
      });
    },

    hasDataErrors() {
      return false;
    },

    hasDiff() {
      return some(this.currentSelections, (isClean, key) => {
        return (
          (this.attributesToClean.has(key) && !isClean) ||
          (!this.attributesToClean.has(key) && isClean)
        );
      });
    },

    isCleanNowDisabled() {
      // Must save selections before clean
      return this.hasDiff || !this.attributesToClean.size;
    },

    hasCleanAttributesUpperCaseEnabled() {
      return get(this.getClientConfig, 'features.cleanAttributesUpperCaseEnabled', false);
    },
  },

  created() {
    this.reset();
  },

  methods: {
    isAttributeSelected(id) {
      if (id in this.currentSelections) {
        return this.currentSelections[id];
      }
      return this.attributesToClean.has(id);
    },

    updateCurrentSelections(id, isClean) {
      this.currentSelections[id] = isClean;
    },

    cleanAttributes() {
      this.$emit('clean-attributes');
    },

    reset() {
      this.currentSelections = {};
      // Set the default value for the auto clean on save checkbox
      // The user selection will be used if it exists, otherwise the config value will be used
      const cleanOnSaveDefault = get(this.clientConfig, 'features.cleanAttributesOnSaveEnabled');
      this.autoCleanOnSave = get(
        this.selectedScenario,
        'cleanAttributesOnSave',
        cleanOnSaveDefault
      );
    },

    onSave() {
      each(this.currentSelections, (isClean, key) => {
        if (this.attributesToClean.has(key) && !isClean) {
          this.attributesToClean.delete(key);
        } else if (!this.attributesToClean.has(key) && isClean) {
          this.attributesToClean.add(key);
        }
      });

      this.currentSelections = {};
      this.$emit('process', this.autoCleanOnSave);
    },

    closeModal() {
      this.reset();
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.clean-attributes {
  &__container {
    font-size: 1.2rem;
    max-height: 500px;
  }
}

:deep(.dialog-card) {
  .dialog-card__footer {
    flex-direction: row-reverse;
  }
}
</style>
